import { useEffect } from "react";

export interface Page {
  data: PageData[];
}
export interface InstagramPage {
  data: InstagramPageData[];
}

export interface PageData {
  picture: PictureData;
  link: string;
  name: string;
  category: string;
  access_token: string;
  id: string;
}

interface PictureData {
  data: PictureUrl;
}

interface PictureUrl {
  url: string;
}

export interface InstagramPageData {
  profile_pic: string;
  username: string;
  id: string;
}

export const useFacebook = () => {
  useEffect(() => {
    const script = document.createElement("script");

    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = `https://connect.facebook.net/en_US/sdk.js`;

    document.body.appendChild(script);

    window.fbAsyncInit = function () {
      FB.init({
        appId: process.env.REACT_APP_FACEBOOK_APP_ID,
        version: "v14.0"
      });
    };

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return {
    login,
    logOut,
    getLoginStatus,
    getPages,
    getPageLink,
    validatePermissions,
    revokePermissions,
    getConnectedInstagramAccounts
  };
};

const login = (scopes: string[]) => {
  return new Promise<fb.StatusResponse>((resolve, reject) => {
    try {
      FB.login(
        response => {
          if (response.status === "connected") resolve(response);
          reject("Facebook login failed. Please try again.");
        },
        {
          scope: scopes.join(","),
          return_scopes: true,
          auth_type: "rerequest"
        }
      );
    } catch (err) {
      reject(
        "Something went wrong while getting response from facebook, please try again later."
      );
    }
  });
};

const validatePermissions = (scopes: string[], grantedScopes: string) => {
  return scopes.filter(x => !grantedScopes?.split(",").includes(x));
};

const getLoginStatus = (ignorCache: boolean) => {
  return new Promise<fb.StatusResponse>((resolve, reject) => {
    try {
      FB.getLoginStatus(function (response) {
        resolve(response);
      }, ignorCache);
    } catch (err) {
      reject(
        "Something went wrong while getting response from facebook, please try again later."
      );
    }
  });
};

const logOut = () => {
  return new Promise<fb.StatusResponse>((resolve, reject) => {
    try {
      FB.logout(function (response) {
        resolve(response);
      });
    } catch (err) {
      reject(
        "Something went wrong while getting response from facebook, please try again later."
      );
    }
  });
};

const getPages = (userId: string) => {
  return new Promise<Page>((resolve, reject) => {
    try {
      FB.api(
        `${userId}/accounts?fields=picture{url},link,name,category,access_token,connected_instagram_account`,
        function (response: Page) {
          resolve(response);
        }
      );
    } catch (err) {
      reject(
        "Something went wrong while getting response from facebook, please try again later."
      );
    }
  });
};

const getConnectedInstagramAccounts = (
  facebookPageId: string,
  pageAccessToke: string
) => {
  return new Promise<InstagramPage>((resolve, reject) => {
    try {
      FB.api(
        `/${facebookPageId}/instagram_accounts?fields=id,username,profile_pic&limit=1000&format=json&method=get&pretty=0&suppress_http_code=1&access_token=${pageAccessToke}`,
        function (response: InstagramPage) {
          resolve(response);
        }
      );
    } catch (err) {
      reject(
        "Something went wrong while getting connected instagram account from facebook, please try again later."
      );
    }
  });
};

const getPageLink = (pageId: string) => {
  return new Promise<fb.Page>((resolve, reject) => {
    try {
      FB.api(`${pageId}?fields=['link','picture']`, function (response) {
        resolve(response);
      });
    } catch (err) {
      reject(
        "Something went wrong while getting page data from facebook, please try again later."
      );
    }
  });
};

const revokePermissions = (userId: string, accessToken: string) => {
  return new Promise((resolve, reject) => {
    try {
      FB.api(
        `/${userId}/permissions`,
        "delete",
        {
          access_token: accessToken
        },
        function (response) {
          resolve(response);
        }
      );
    } catch (err) {
      reject(
        "Something went wrong while revoking permissions, please try again later."
      );
    }
  });
};
